"use client";

import { Link } from "@/i18n/navigation";
import { AdminButton } from "./AdminButton";
import { MyFiles } from "./MyFiles";
import { NavbarUserDropdown } from "./NavbarUserDropdown";
import { StudyResourceTitle } from "./StudyResourceTitle";
import Share from "@/components/modals/Share";
import { useFileExplorer } from "@/providers/file-explorer-provider";
import { useUser } from "@/auth/hooks/clerk-hooks";
import { Share2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

interface AuthNavbarProps {
	pathname: string;
	is_signed_in: boolean;
	is_lti_mode: boolean;
	user_role?: string | null;
	display_role?: string | null;
}

export const AuthNavbar = ({
	pathname,
	is_signed_in,
	is_lti_mode,
	user_role,
	display_role,
}: AuthNavbarProps) => {
	const { user } = useUser();
	const query_client = useQueryClient();
	const { current_file, set__current_file, share_open, set__share_modal_open, is_editable } = useFileExplorer();
	const path_segments = pathname.split("/").filter(Boolean);
	const resource_slug = path_segments[0] === "study" ? path_segments[1] : undefined;
	const is_on_study_resource = path_segments[0] === "study" && Boolean(resource_slug) && resource_slug !== "create";
	const safe_user = user ?? { email: "" };

	const open_share_modal = () => {
		if (!resource_slug) return;

		const is_current_resource =
			current_file?.type === "resource" &&
			current_file?.slug === resource_slug;

		if (!is_current_resource) {
			const cached_resource = query_client.getQueryData<{ id?: number; name?: string }>([
				"resource",
				resource_slug,
			]);
			const resource_name =
				typeof cached_resource?.name === "string" && cached_resource.name.trim().length > 0
					? cached_resource.name
					: "Resource";
			set__current_file({
				slug: resource_slug,
				type: "resource",
				name: resource_name,
			});
		}

		set__share_modal_open(true);
	};

	return (
		<>
			<nav
				aria-label="Authenticated navigation"
				className="top-0 left-0 right-0 z-60 border-b border-slate-200 bg-white px-2 sm:px-2.5 md:px-3"
			>
				<div className="mx-auto flex h-[60px] items-center justify-between">
					<div className="flex min-w-0 items-center gap-2 sm:gap-3">
						{is_on_study_resource ? (
							<>
								<Link
									key="resource-logo"
									href="/app"
									className="shrink-0 rounded-lg p-1 transition-colors hover:bg-slate-100"
								>
									<img
										src="/favicon-32x32.png"
										alt="StudyStash favicon"
										width={32}
										height={32}
										className="h-8 w-8 object-contain"
									/>
								</Link>
								<div className="flex min-w-0 items-center gap-1">
									<StudyResourceTitle resource_slug={resource_slug} />
									{is_editable && (
										<button
											type="button"
											className="group shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
											onClick={open_share_modal}
											aria-label="Share resource"
										>
											<Share2 className="h-4 w-4" />
										</button>
									)}
								</div>
							</>
						) : (
							<Link
								key="app-logo"
								href="/app"
								className="shrink-0 rounded-lg p-1 transition-colors hover:bg-slate-100"
							>
								<img
									src="/StudyStash - Logo_Colour - RGB.png"
									alt="StudyStash Brand Mark"
									width={124}
									height={26}
									className="h-auto w-[124px] object-contain sm:w-[170px]"
								/>
							</Link>
						)}
					</div>

					<div className="ml-2 flex items-center gap-2 sm:gap-3">
						{!pathname.startsWith("/app") ? (
							<div className={is_on_study_resource ? "flex" : "hidden sm:flex"}>
								<MyFiles />
							</div>
						) : null}
						{["org:admin", "org:superadmin"].includes(user_role || "") ? (
							<div className="hidden md:flex">
								<AdminButton />
							</div>
						) : null}

						{is_signed_in ? <NavbarUserDropdown is_lti_mode={is_lti_mode} /> : null}
					</div>
				</div>
			</nav>

			{is_on_study_resource ? (
				<Share user={safe_user} share_open={share_open} set__share_open={set__share_modal_open} />
			) : null}
		</>
	);
};
